import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../constants";

/**
 * AccessLog — most-recent CRM access events.
 * Backed by GET /crm/access-log. Refresh button re-fetches.
 */
export default function AccessLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/crm/access-log`, { withCredentials: true });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error("Failed to load access log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch { return iso; }
  };

  const badge = (type) => {
    const isCreator = type === "creator";
    const cls = isCreator
      ? "bg-purple-500/20 text-purple-300 border border-purple-500/40"
      : "bg-[#A3E635]/20 text-[#A3E635] border border-[#A3E635]/40";
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${cls}`}>
        {isCreator ? "Creator" : "Administrator"}
      </span>
    );
  };

  return (
    <div className="card-glass p-6" data-testid="crm-access-log">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white">Access Log</h2>
          <p className="text-[#E8E4DC]/60 text-sm mt-1">
            Every successful /crm sign-in. Most recent first.
          </p>
        </div>
        <button
          onClick={load}
          className="btn-primary text-sm px-4 py-2"
          data-testid="crm-access-log-refresh"
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {items.length === 0 && !loading && (
        <p className="text-[#E8E4DC]/60 text-sm py-6 text-center">No access events recorded yet.</p>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#E8E4DC]/60 border-b border-white/10">
                <th className="py-2 pr-4 font-medium">When</th>
                <th className="py-2 pr-4 font-medium">Code</th>
                <th className="py-2 pr-4 font-medium">IP</th>
                <th className="py-2 pr-4 font-medium">User Agent</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-2 pr-4 text-white whitespace-nowrap">{fmt(row.timestamp)}</td>
                  <td className="py-2 pr-4">{badge(row.code_type)}</td>
                  <td className="py-2 pr-4 text-[#E8E4DC] font-mono text-xs">{row.ip || "—"}</td>
                  <td className="py-2 pr-4 text-[#E8E4DC]/70 text-xs truncate max-w-[420px]" title={row.user_agent}>
                    {row.user_agent || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
